import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Download, FileSpreadsheet } from 'lucide-react';
import {
  getGetApiV1ExportsQueryKey,
  useGetApiV1Exports,
  usePostApiV1Exports,
} from '@/shared/api/generated/exports/exports';
import { ExportKind, ExportStatus } from '@/shared/api/generated/model';
import type { ExportJobDto } from '@/shared/api/generated/model';
import { useLocale } from '@/shared/lib/i18n/locale-context';
import { Button } from '@/shared/ui/Button';
import { Chip } from '@/shared/ui/Chip';
import { EmptyState } from '@/shared/ui/EmptyState';
import { Field } from '@/shared/ui/Field';
import { PageHeader } from '@/shared/ui/PageHeader';
import { Panel } from '@/shared/ui/Panel';
import { Pagination } from '@/shared/ui/Pagination';
import { QueryError } from '@/shared/ui/QueryError';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table';
import { TableSkeleton } from '@/shared/ui/TableSkeleton';

const PAGE_SIZE = 25;

const KINDS = [ExportKind.Missions, ExportKind.Volunteers, ExportKind.Participation] as const;

/**
 * How often the panel asks again while something is being built.
 *
 * ⚠️ Only while something is unfinished — see the query below. A screen that polled forever would
 * keep a request per open tab going all day for a table that changes when somebody clicks a button.
 */
const POLL_MS = 3000;

/**
 * The association's exports (§19), through §13's `202 → poll → 200 with a download link`.
 *
 * ⚠️ **The request is not the file.** The server answers `202` and builds in the background, so this
 * screen's job is to keep asking and then hand over a link — never to wait on a response. An export
 * of a year of participation reads more rows than an HTTP request should be alive for.
 */
export function ExportsPanel() {
  const { t, locale } = useLocale();
  const queryClient = useQueryClient();

  const [kind, setKind] = useState<ExportKind>(ExportKind.Missions);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);
  const [failed, setFailed] = useState(false);

  const { data, isPending, isError, error, refetch } = useGetApiV1Exports(
    { Page: page, PageSize: PAGE_SIZE },
    {
      query: {
        refetchInterval: (query) =>
          (query.state.data?.items ?? []).some(isUnfinished) ? POLL_MS : false,
      },
    },
  );

  const ask = usePostApiV1Exports({
    mutation: {
      onSuccess: async () => {
        setFailed(false);
        setPage(1);
        await queryClient.invalidateQueries({ queryKey: getGetApiV1ExportsQueryKey() });
      },
      onError: () => setFailed(true),
    },
  });

  const submit = () => {
    ask.mutate({
      data: {
        kind,
        // ⚠️ Instants, not dates. The server stores every timestamp in UTC (§3) and cannot know which
        // day boundary the reader meant; the browser can, so the conversion happens where the
        // calendar is. `to` is the END of its day — a range that stopped at midnight would silently
        // drop everything that happened on the last day the operator asked for.
        ...(from ? { from: startOfDay(from) } : {}),
        ...(to ? { to: endOfDay(to) } : {}),
      },
    });
  };

  return (
    <div className="space-y-6">
      <PageHeader title={t.exports.title} lede={t.exports.subtitle} />

      <Panel title={t.exports.ask} description={t.exports.askHint}>
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[220px] flex-1">
            <Field label={t.exports.kind} htmlFor="export-kind">
              <Select value={kind} onValueChange={(value) => setKind(value as ExportKind)}>
                <SelectTrigger id="export-kind">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {KINDS.map((value) => (
                    <SelectItem key={value} value={value}>
                      {t.exports.kinds[value]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>

          <div className="min-w-[160px]">
            <Field label={t.exports.from} htmlFor="export-from">
              <input
                id="export-from"
                type="date"
                className="h-11 w-full rounded-lg border border-gray-200 bg-white px-3 text-sm outline-none focus:border-brand-300"
                value={from}
                onChange={(event) => setFrom(event.target.value)}
              />
            </Field>
          </div>

          <div className="min-w-[160px]">
            <Field label={t.exports.to} htmlFor="export-to">
              <input
                id="export-to"
                type="date"
                className="h-11 w-full rounded-lg border border-gray-200 bg-white px-3 text-sm outline-none focus:border-brand-300"
                value={to}
                onChange={(event) => setTo(event.target.value)}
              />
            </Field>
          </div>

          <Button onClick={submit} disabled={ask.isPending}>
            <FileSpreadsheet className="h-4 w-4" />
            {ask.isPending ? t.exports.asking : t.exports.askAction}
          </Button>
        </div>

        {failed && <p className="text-sm text-error-500">{t.exports.askFailed}</p>}
      </Panel>

      {isError && <QueryError error={error} onRetry={() => void refetch()} />}

      <Panel title={t.exports.requests} bodyClassName="flex flex-col">
        {/*
          ⚠️ A refused query must not answer «لم يُطلب أيّ تصدير بعد» — that is a statement about
          what the association has done, made from a request that never returned. Error before
          empty, as `RoleManager` already does.
        */}
        {isError ? null : isPending ? (
          <TableSkeleton rows={5} columns={6} />
        ) : data && data.items && data.items.length > 0 ? (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t.exports.kind}</TableHead>
                  <TableHead>{t.exports.range}</TableHead>
                  <TableHead>{t.exports.status}</TableHead>
                  <TableHead>{t.exports.rows}</TableHead>
                  <TableHead>{t.exports.requestedAt}</TableHead>
                  <TableHead>{t.exports.file}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.items.map((job) => (
                  <Row key={job.id} job={job} locale={locale} />
                ))}
              </TableBody>
            </Table>

            <Pagination
              page={data.page ?? page}
              pageSize={data.pageSize ?? PAGE_SIZE}
              totalCount={data.totalCount ?? 0}
              onPageChange={setPage}
            />
          </>
        ) : (
          <EmptyState
            icon={FileSpreadsheet}
            title={t.exports.emptyTitle}
            hint={t.exports.empty}
          />
        )}
      </Panel>
    </div>
  );
}

function Row({ job, locale }: { job: ExportJobDto; locale: string }) {
  const { t } = useLocale();

  return (
    <TableRow>
      <TableCell className="text-sm">{t.exports.kinds[job.kind]}</TableCell>

      <TableCell className="whitespace-nowrap text-sm text-gray-500">
        {job.from || job.to
          ? `${day(job.from, locale, t.exports.openStart)} — ${day(job.to, locale, t.exports.openEnd)}`
          : t.exports.everything}
      </TableCell>

      <TableCell>
        <Chip tone={toneFor(job.status)}>{t.exports.statuses[job.status]}</Chip>
      </TableCell>

      <TableCell className="text-sm">
        {/* ⚠️ Zero is shown, not hidden behind a dash. An export that matched nothing is a legitimate
            answer, and it looks exactly like a failure until the count says otherwise. */}
        {job.rowCount ?? <span className="text-gray-400">—</span>}
      </TableCell>

      <TableCell className="whitespace-nowrap text-sm text-gray-500">
        {/* ⚠️ Gregorian and explicit: an unqualified date on a machine set to Umm al-Qura renders
            Hijri, and this timestamp is compared against the server's own log. */}
        {new Date(job.requestedAt).toLocaleString(`${locale}-u-ca-gregory`)}
      </TableCell>

      <TableCell>
        {job.status === ExportStatus.Ready && job.downloadUrl ? (
          <a
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-500 hover:text-brand-600"
            href={job.downloadUrl}
            download={job.fileName ?? undefined}
          >
            <Download className="h-4 w-4" />
            {t.exports.download}
          </a>
        ) : job.status === ExportStatus.Failed ? (
          // ⚠️ The reason, in full. An export that silently never finishes is the same to an
          // administrator as one still running — and they would wait.
          <span className="text-sm text-error-500">{job.failureReason ?? t.exports.failed}</span>
        ) : (
          <span className="text-sm text-gray-400">{t.exports.building}</span>
        )}
      </TableCell>
    </TableRow>
  );
}

const isUnfinished = (job: ExportJobDto) =>
  job.status === ExportStatus.Queued || job.status === ExportStatus.Building;

function toneFor(status: ExportStatus) {
  switch (status) {
    case ExportStatus.Ready:
      return 'approved' as const;
    case ExportStatus.Failed:
      return 'rejected' as const;
    default:
      return 'pending' as const;
  }
}

function day(value: string | null | undefined, locale: string, fallback: string): string {
  return value ? new Date(value).toLocaleDateString(`${locale}-u-ca-gregory`) : fallback;
}

/**
 * ⚠️ Built from the parts rather than parsed. `new Date('2026-08-26')` is parsed as UTC midnight by
 * specification, while `new Date(2026, 7, 26)` is local midnight — so the shorter version shifts the
 * boundary by the reader's own offset, in the direction that quietly drops a day.
 */
function startOfDay(value: string): string {
  return boundary(value, 0, 0, 0, 0);
}

function endOfDay(value: string): string {
  return boundary(value, 23, 59, 59, 999);
}

function boundary(
  value: string,
  hours: number,
  minutes: number,
  seconds: number,
  milliseconds: number,
): string {
  const [year = 0, month = 1, dayOfMonth = 1] = value.split('-').map(Number);

  return new Date(year, month - 1, dayOfMonth, hours, minutes, seconds, milliseconds).toISOString();
}
